import { jsx } from "react/jsx-runtime";
import { cn } from "../../../lib/utils.js";
import { memo, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { QUIZ_INLINE_STYLES } from "./quiz-styles.js";
function arePropsEqual(prevProps, nextProps) {
  const { index } = prevProps;
  const prevValue = prevProps.answers[index] || "";
  const nextValue = nextProps.answers[index] || "";
  if (prevValue !== nextValue) return false;
  const prevIsActive = prevProps.currentIndex === index && !prevProps.isSolved;
  const nextIsActive = nextProps.currentIndex === index && !nextProps.isSolved;
  if (prevIsActive !== nextIsActive) return false;
  if (prevProps.isSolved !== nextProps.isSolved) return false;
  if (prevProps.isCorrect !== nextProps.isCorrect) return false;
  if (prevProps.showCorrectAnswer !== nextProps.showCorrectAnswer) return false;
  if (prevProps.correctAnswer !== nextProps.correctAnswer) return false;
  if (prevProps.useKeypad !== nextProps.useKeypad) return false;
  if (prevProps.isViewSolved !== nextProps.isViewSolved) return false;
  if (prevProps.minWidth !== nextProps.minWidth) return false;
  if (prevProps.width !== nextProps.width) return false;
  if (prevProps.height !== nextProps.height) return false;
  return true;
}
const QuizBlank = memo(function QuizBlank2({
  index,
  answers,
  currentIndex,
  isCorrect,
  isSolved,
  showCorrectAnswer,
  correctAnswer,
  onClick,
  onChange,
  useKeypad = false,
  minWidth = "56px",
  width,
  isViewSolved = false,
  height
}) {
  const { t } = useTranslation("quiz");
  const inputRef = useRef(null);
  const value = answers[index] || "";
  const isActive = currentIndex === index && !isSolved;
  const hasValue = value !== "";
  const displayValue = showCorrectAnswer && isSolved && correctAnswer ? correctAnswer : value;
  useEffect(() => {
    if (isActive && !useKeypad && inputRef.current) {
      inputRef.current.focus({ preventScroll: true });
    }
  }, [isActive, useKeypad]);
  if (!useKeypad && !isSolved && !isViewSolved) {
    const inputSize = Math.max(value.length || 4, 4);
    return /* @__PURE__ */ jsx(
      "input",
      {
        ref: inputRef,
        type: "text",
        value,
        onChange: (e) => onChange == null ? void 0 : onChange(e.target.value),
        onClick,
        placeholder: t("shared.blank"),
        size: inputSize,
        className: cn(
          "inline-flex items-center justify-center py-1 px-2 align-middle bg-white border rounded-lg mx-1 text-base font-semibold text-gray-900 transition-all text-center outline-hidden",
          QUIZ_INLINE_STYLES.inputPlaceholder,
          "cursor-text",
          // 기본 상태
          !isActive && !hasValue && "border-gray-300",
          isActive && "border-blue-500 ring-2 ring-blue-100",
          hasValue && !isActive && "border-blue-500 bg-blue-50 text-blue-700"
        ),
        style: {
          minWidth: width || minWidth,
          height: height || "auto"
        }
      }
    );
  }
  return /* @__PURE__ */ jsx(
    "span",
    {
      onClick,
      className: cn(
        "inline-flex items-center justify-center py-1 px-3 align-middle bg-white border rounded-lg mx-1 text-base font-semibold text-gray-900 transition-all text-center outline-hidden",
        !isSolved && !isActive && !hasValue && "border-gray-300 cursor-pointer",
        isActive && "border-blue-500 ring-2 ring-blue-100 scale-105 cursor-pointer",
        !isSolved && hasValue && !isActive && "border-blue-500 bg-blue-50 text-blue-700 cursor-pointer",
        // 정답보기 모드 - 전체 빈칸을 정답(초록색)으로 표시
        showCorrectAnswer && isSolved && QUIZ_INLINE_STYLES.correct,
        // 정답 (내 답안이 맞음) - 정답보기 모드가 아닐 때
        !showCorrectAnswer && isSolved && isCorrect === true && QUIZ_INLINE_STYLES.correct,
        // 오답 (정답보기 모드가 아닐 때만 빨강)
        !showCorrectAnswer && isSolved && isCorrect === false && QUIZ_INLINE_STYLES.wrong,
        // 비활성화
        (isSolved || isViewSolved) && "pointer-events-none"
      ),
      style: {
        minWidth: width || minWidth,
        height: height || "auto"
      },
      children: displayValue || /* @__PURE__ */ jsx("span", { className: QUIZ_INLINE_STYLES.boxEmpty, children: t("shared.blank") })
    }
  );
}, arePropsEqual);
export {
  QuizBlank
};
//# sourceMappingURL=quiz-blank.js.map
